import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { Modal } from '../../../shared/components/Modal';
import { StageChip } from './StageChip';
import { useStorefrontStatus, useProvisionInfra, useGoLive } from '../hooks/useOnboarding';
import { getAppEnvironment } from '../../../shared/utils/appEnv';
import type { StorefrontStatus } from '../types';

const readErr = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

interface MerchantStorefrontProps {
  /** The merchant's canonical slug — keys the provisioning status + dispatch. */
  slug: string;
  /** Whether the current user may dispatch infra provisioning (MERCHANT.CREATE). */
  canProvision: boolean;
}

/**
 * Per-merchant storefront standup + provisioning, shown inside the merchant detail page.
 * Reads live status (tenant · infra · deploy) for this merchant's slug and lets an
 * authorised user dispatch/re-dispatch the Terraform infra workflow. Replaces the old
 * standalone Storefront Onboarding board (which listed a hardcoded set of merchants) —
 * storefront state now lives with the merchant it belongs to.
 */
export function MerchantStorefront({ slug, canProvision }: MerchantStorefrontProps) {
  const { data: status, isLoading, isError, refetch, isFetching } = useStorefrontStatus(slug);
  const provisionInfra = useProvisionInfra(slug);
  const goLive = useGoLive(slug);
  const infraDone = status?.infra.repoProvisioned && status?.infra.siteProvisioned;
  const isLive = !!status?.deploy.live;

  const [confirmOpen, setConfirmOpen] = useState(false);
  // Set from a refused (409) un-forced attempt — its presence switches the modal
  // to the "force" affordance so the operator can override a stale readiness gate.
  const [blocker, setBlocker] = useState<string | null>(null);

  const closeConfirm = () => {
    setConfirmOpen(false);
    setBlocker(null);
  };

  const attemptGoLive = async (force: boolean) => {
    try {
      // Always target THIS Osprey instance's environment. Omitting it makes the
      // ops workflow default to production — deploying a dev/staging tenant to
      // prod (or failing on missing prod secrets). See getAppEnvironment().
      await goLive.mutateAsync({ environment: getAppEnvironment(), ...(force ? { force: true } : {}) });
      closeConfirm();
    } catch (err) {
      // Readiness gate refused (or transient) — surface why and offer to force.
      setBlocker(readErr(err, 'Go-live was refused. Check the prerequisites and try forcing.'));
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">Storefront</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Standup status for this merchant's personalised storefront (tenant · infra · deploy).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-1.5 rounded-md hover:bg-muted text-foreground transition-colors disabled:opacity-50"
            aria-label="Refresh storefront status"
            title="Refresh status"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={isFetching ? 'animate-spin' : ''}>
              <path d="M21 12a9 9 0 1 1-2.64-6.36" strokeLinecap="round" />
              <path d="M21 3v6h-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {canProvision && (
            <Button
              size="sm"
              variant={infraDone ? 'outline' : 'primary'}
              loading={provisionInfra.isPending}
              disabled={provisionInfra.isPending || isError}
              onClick={() => provisionInfra.mutate()}
            >
              {infraDone ? 'Re-provision infra' : 'Provision infra'}
            </Button>
          )}
          {canProvision && !isLive && (
            <Button
              size="sm"
              variant="primary"
              loading={goLive.isPending}
              disabled={goLive.isPending || isError || !status}
              onClick={() => setConfirmOpen(true)}
            >
              Take live
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading storefront status…</p>
      ) : isError ? (
        <p className="text-sm text-red-500">Storefront status unavailable.</p>
      ) : status ? (
        <dl className="space-y-3">
          <StageRow label="Tenant">
            <StageChip label="Created" tone={status.tenant.created ? 'done' : 'idle'} />
            <StageChip label="Onboarded" tone={status.tenant.onboarded ? 'done' : status.tenant.created ? 'pending' : 'idle'} />
            <StageChip label="Key" tone={status.tenant.keyIssued ? 'done' : status.tenant.onboarded ? 'pending' : 'idle'} />
          </StageRow>
          <StageRow label="Infra">
            <StageChip label="Repo" tone={status.infra.repoProvisioned ? 'done' : status.provisioning ? 'pending' : 'idle'} />
            <StageChip label="Site" tone={status.infra.siteProvisioned ? 'done' : status.provisioning ? 'pending' : 'idle'} />
          </StageRow>
          <StageRow label="Deploy">
            <DeployStatus status={status} />
          </StageRow>
        </dl>
      ) : null}

      <Modal isOpen={confirmOpen} onClose={closeConfirm} size="sm">
        <div className="p-6">
          <h3 className="text-base font-semibold text-foreground">Take storefront live?</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            This flips <span className="font-medium text-foreground">{slug}</span> out of demo mode
            (<code>DEMO_MODE=false</code>) and redeploys its Cloudflare Pages project. The store
            becomes publicly indexable and starts serving real backend data on the same URL.
          </p>

          {status && (
            <ul className="mt-4 space-y-1.5">
              <ReadinessRow ok={status.tenant.created} label="Tenant created in Watchman" />
              <ReadinessRow ok={status.tenant.keyIssued} label="Storefront API key issued" />
              <ReadinessRow ok={status.infra.repoProvisioned} label="Storefront repo reachable" />
              <ReadinessRow ok={status.infra.siteProvisioned} label="Cloudflare Pages project provisioned" />
            </ul>
          )}

          {blocker && (
            <div className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
              <p className="font-medium">Go-live was refused</p>
              <p className="mt-0.5">{blocker}</p>
              <p className="mt-1 text-xs">
                If you know the tenant is ready (e.g. the key check is a known false-negative),
                force the flip below.
              </p>
            </div>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <Button size="sm" variant="outline" disabled={goLive.isPending} onClick={closeConfirm}>
              Cancel
            </Button>
            {blocker ? (
              <Button
                size="sm"
                variant="primary"
                loading={goLive.isPending}
                disabled={goLive.isPending}
                onClick={() => attemptGoLive(true)}
              >
                Force go-live
              </Button>
            ) : (
              <Button
                size="sm"
                variant="primary"
                loading={goLive.isPending}
                disabled={goLive.isPending}
                onClick={() => attemptGoLive(false)}
              >
                Take live
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}

function ReadinessRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      <span
        className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
          ok ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'
        }`}
        aria-hidden="true"
      >
        {ok ? '✓' : '–'}
      </span>
      <span className={ok ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
    </li>
  );
}

function StageRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-4">
      <dt className="w-20 shrink-0 text-sm font-medium text-foreground">{label}</dt>
      <dd className="flex flex-wrap gap-1.5">{children}</dd>
    </div>
  );
}

function DeployStatus({ status }: { status: StorefrontStatus }) {
  const { live, url } = status.deploy;
  if (live && url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 hover:underline"
      >
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Live
      </a>
    );
  }
  return <StageChip label="Not live" tone="idle" />;
}
